import { NodeDefinition } from '../../types'
import { SwitchEditorNode } from './switch.editorNode'
import { SwitchIcon } from './switch.icon'
import { SwitchConfigForm } from './switch.configForm'
import {
  collectSupportingTasks,
  createBlankGroup,
  createSwitchExpression,
  createSwitchHandleMapping,
  pruneUnusedInputs,
  resolveSwitchConditionalProperties,
  wrapSupportingBranchesInFork
} from './helpers'
import { ELSE_HANDLE_ID } from './switch.constants'
import {
  createSwitchTask,
  getTaskReferenceName,
  wrapVariables
} from 'Automations/nodeDefinitions/conductor'
import {
  getClosestMatch,
  type SemanticType
} from 'Automations/nodeDefinitions/semanticTypes'
import {
  conditionPropertyRequired,
  conditionOperatorRequired,
  conditionValueRequired,
  handlesConnected
} from './switch.validators'
import { getUpstreamConditionalProperties } from 'Automations/nodeDefinitions/conditions/helpers'
import {
  findDownstreamNodes,
  getStartNode,
  transformNodeGraphToIntermediateNodeArray
} from 'Automations/nodeDefinitions/helpers'
import type { AutomationsPresentationV1 } from 'Automations/automationEditor/types'
import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import type { ResolvedNodeDefinitions } from 'Automations/nodeDefinitions/types'
import { SwitchExecutionSummary } from './Switch.executionSummary'
import type { Condition, Config, SwitchGroup } from './switch.types'

// Re-exported for existing consumers; new code should import straight from
// `./switch.types` so it doesn't pull this module's node definition too.
export type { Condition, Config, SwitchGroup }

type TaskName = 'switch'

const taskReferenceBuilder = getTaskReferenceName<TaskName>('SWITCH')

export const switchNodeDefinition: NodeDefinition<Config> = {
  name: 'Switch',
  editorNode: SwitchEditorNode,
  configForm: SwitchConfigForm,
  icon: SwitchIcon,
  defaultConfig: {
    name: '',
    groups: [createBlankGroup()]
  },
  validation: {
    node: { ERROR: [], WARN: [] },
    config: {
      name: { ERROR: [], WARN: [] },
      groups: {
        ERROR: [],
        WARN: [],
        items: {
          conditions: {
            ERROR: [],
            WARN: [],
            items: {
              property: { ERROR: [conditionPropertyRequired], WARN: [] },
              operator: { ERROR: [conditionOperatorRequired], WARN: [] },
              value: { ERROR: [conditionValueRequired], WARN: [] }
            }
          }
        }
      }
    },
    handles: { ERROR: [handlesConnected], WARN: [] }
  },
  buildValidationScope: ({ node, presentation, definitions }) => ({
    allProperties: getUpstreamConditionalProperties({
      nodeId: node.id,
      presentation,
      definitions
    }),
    groups: node.data.groups,
    nodes: presentation.nodes,
    // A property whose subject is another node validates where that node sits
    // — a review step must come after the switch that reads its approval.
    downstreamNodes: downstreamNodesFor({ node, presentation, definitions })
  }),
  inputMapping: ({ upstreamNodes }) => {
    // Expose every semantic type surfaced by any upstream node's outputs
    // (i.e. the `outputMapping` return on each start/action node) so
    // operator factory configs can reference any of them — currency,
    // percentage base, etc. — without the switch having to know in advance
    // which conditions a user will configure.
    const allSemanticTypes = Array.from(
      new Set(
        upstreamNodes.flatMap(node =>
          Object.values(node.outputs).map(o => o.semanticType)
        )
      )
    ) as SemanticType[]

    return allSemanticTypes.reduce<Record<string, string>>(
      (acc, semanticType) => ({
        ...acc,
        [semanticType]: getClosestMatch({ semanticType, upstreamNodes })
      }),
      {}
    )
  },
  handleMapping: ({ config }) => createSwitchHandleMapping(config.groups),
  taskTransformer: ({
    nodeId,
    inputMapping,
    branches,
    config,
    upstreamNodes,
    nodes
  }) => {
    const taskReferenceName = taskReferenceBuilder(nodeId)

    const handleMapping = createSwitchHandleMapping(config.groups)
    const conditionalProperties = resolveSwitchConditionalProperties({
      upstreamNodes
    })

    const elseIndex = handleMapping[ELSE_HANDLE_ID]

    // Conditions whose comparison needs data the trigger doesn't carry (e.g. a
    // user-chosen quote template) contribute a branch here, merged on reference
    // name so N rows on the same subject cost one branch. `inputMapping` goes in
    // so a branch can read an upstream task's output — the compare task's quote
    // side, for instance.
    const { branches: supportingBranches, semanticTypeMapping } =
      collectSupportingTasks({
        nodeId,
        groups: config.groups,
        conditionalProperties,
        inputMapping,
        nodes
      })

    const expression = createSwitchExpression({
      groups: config.groups,
      conditionalProperties
    })

    const usedInputMapping = pruneUnusedInputs({
      // Supporting-task entries are merged last so they win any key collision —
      // the "prioritise the result of the task" rule. Their keys are scoped per
      // subject, so in practice a collision can't arise.
      inputMapping: { ...inputMapping, ...semanticTypeMapping },
      expression
    })

    return [
      // Supporting fetches run before the switch is evaluated. The switch task
      // must stay last: when a switch sits inside a parallel branch,
      // `wrapBranchesInForkJoin` joins on the branch's final task.
      ...wrapSupportingBranchesInFork({ nodeId, branches: supportingBranches }),
      createSwitchTask({
        name: 'switch',
        taskReferenceName: taskReferenceName('switch'),
        evaluatorType: 'javascript',
        inputParameters: wrapVariables(usedInputMapping),
        expression,
        decisionCases: Object.fromEntries(
          Object.entries(handleMapping)
            .filter(([key]) => key !== ELSE_HANDLE_ID)
            .map(([key, index]) => [key, branches[index]])
        ),
        defaultCase: branches[elseIndex] ?? []
      })
    ]
  },
  executionSummary: SwitchExecutionSummary
}

/**
 * The nodes this switch leads to, or none when the graph can't be walked —
 * validation runs on every edit, including while the canvas is still a
 * disconnected node or two with no start.
 */
const downstreamNodesFor = ({
  node,
  presentation,
  definitions
}: {
  node: AutomationsPresentationV1Node
  presentation: AutomationsPresentationV1
  definitions: ResolvedNodeDefinitions
}): AutomationsPresentationV1Node[] => {
  if (!getStartNode(presentation, definitions)) return []

  const graph =
    transformNodeGraphToIntermediateNodeArray(definitions)(presentation)
  return findDownstreamNodes(graph, node.id) ?? []
}
